import { Link, useParams } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";

/**
 * Legal pages — one route, three topics.
 * Content is intentionally plain (no admin CMS), because law text should not be
 * accidentally edited from the backoffice. Real legal review required before launch.
 *
 * Company entity: Carolina Pelicano · NIF 225882299
 * Registered address: Alameda Quinta de Santo António 5, núcleo 2, 9º Esq · 1600-675 Lisboa
 * Contact: goyalisboapt@gmail.com
 */

const COMPANY = {
  name: "Carolina Pelicano",
  address: "Alameda Quinta de Santo António 5, núcleo 2, 9º Esquerdo, 1600-675 Lisboa, Portugal",
  nif: "225882299",
  email: "goyalisboapt@gmail.com",
  site: "GOYA LISBOA",
};

const TITLES = {
  terms: "Terms & Conditions",
  privacy: "Privacy Policy",
  cookies: "Cookie Policy",
};

const Terms = () => (
  <>
    <p>
      These Terms &amp; Conditions govern your use of the {COMPANY.site} website and the purchase of goods offered through it.
      By placing an order you accept these Terms in full.
    </p>
    <h2>1. Seller identification</h2>
    <p>
      <strong>{COMPANY.name}</strong>, NIF {COMPANY.nif}, registered at {COMPANY.address}.
      Contact: <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>.
    </p>
    <h2>2. Products & pricing</h2>
    <p>
      All prices are shown in Euro (EUR) and include Portuguese VAT (IVA) at the applicable rate. Product images are illustrative;
      small variations in colour and finish may occur due to the nature of the pieces we curate. We reserve the right to correct
      any typographical errors in price or description before an order is confirmed.
    </p>
    <h2>3. Orders & payment</h2>
    <p>
      Payments are processed by Stripe. We accept the following methods: Visa, Mastercard,
      American Express, Multibanco and MB WAY. Additional methods may be shown at checkout depending on your
      country. An order is only binding once we send you the order-confirmation email. If a piece becomes
      unavailable after your order, we will contact you to offer an alternative or a full refund.
    </p>
    <h2>4. Shipping</h2>
    <p>
      We ship from Lisbon within 1–2 business days of order confirmation. Delivery windows and fees are shown at checkout and on our
      Shipping page. Risk of loss transfers to you upon delivery.
    </p>
    <h2>5. Right of withdrawal (returns)</h2>
    <p>
      Under EU consumer law (Decree-Law 24/2014), you have 14 days from the day you receive the goods to withdraw from the contract
      without giving any reason. We voluntarily extend this window to 30 days. Items must be returned unworn, with tags, and in
      the original packaging. Sale items and personalised pieces are excluded from returns where legally permitted.
    </p>
    <p>
      To exercise your right, email <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a> with your order number. Refunds are
      issued within 14 days of us receiving the returned goods, using the same payment method as the original order.
    </p>
    <h2>6. Warranty</h2>
    <p>
      All products carry the legal conformity warranty of 3 years for consumers established in the EU. Defects must be reported to
      us within 2 months of discovery.
    </p>
    <h2>7. Governing law & dispute resolution</h2>
    <p>
      These Terms are governed by the laws of Portugal. Under Portuguese consumer law (Lei n.º 144/2015),
      consumers may access the Electronic Complaints Book at{" "}
      <a href="https://www.livroreclamacoes.pt" target="_blank" rel="noreferrer">livroreclamacoes.pt</a>,
      submit disputes to the Portuguese national alternative dispute resolution centre (CNIACC —{" "}
      <a href="https://www.cniacc.pt" target="_blank" rel="noreferrer">cniacc.pt</a>), or use the EU Online
      Dispute Resolution platform at{" "}
      <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noreferrer">ec.europa.eu/consumers/odr</a>.
    </p>
    <h2>8. Contact</h2>
    <p>
      Questions? Email us at <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>.
    </p>
  </>
);

const Privacy = () => (
  <>
    <p>
      This Privacy Policy explains what personal data <strong>{COMPANY.name}</strong> (the “Controller”) collects when you visit
      {" "}{COMPANY.site} and how we use it. This policy complies with the EU General Data Protection Regulation (GDPR) and
      the Portuguese Data Protection Law (Lei 58/2019).
    </p>
    <h2>1. Data controller</h2>
    <p>
      {COMPANY.name} · NIF {COMPANY.nif} · {COMPANY.address} · <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>.
    </p>
    <h2>2. What we collect</h2>
    <ul>
      <li><strong>Account data</strong> — name, email, and (for password sign-in) a hashed password. For Google sign-in we also
        store the email and public profile picture.</li>
      <li><strong>Order data</strong> — shipping address, phone (if provided), items purchased, and payment status. Card details
        are handled by Stripe and never touch our servers.</li>
      <li><strong>Marketing</strong> — email address if you subscribe to the newsletter.</li>
      <li><strong>Technical</strong> — IP address, browser and device information, referrer, and pages viewed (see Cookie Policy).</li>
    </ul>
    <h2>3. Why we process it</h2>
    <ul>
      <li>To fulfil your orders (contract performance).</li>
      <li>To provide the account features you request (contract performance).</li>
      <li>To send transactional emails: order confirmation, shipping notification, refund confirmation (legitimate interest / contract).</li>
      <li>To send marketing emails only after explicit consent, and always with a one-click unsubscribe link.</li>
      <li>To comply with our tax and accounting obligations (legal obligation).</li>
    </ul>
    <h2>4. Who we share it with</h2>
    <p>
      We use trusted service providers, contracted under standard EU data protection clauses: <strong>Stripe</strong> (payments),
      <strong>MongoDB Atlas</strong> (database, EU region), <strong>Cloudinary</strong> (image hosting), <strong>Resend</strong>{" "}
      (email delivery), and <strong>Netlify / Render</strong> (hosting). We never sell your data.
    </p>
    <h2>5. How long we keep it</h2>
    <ul>
      <li>Account data — until you request deletion.</li>
      <li>Order data — 10 years (Portuguese fiscal law).</li>
      <li>Marketing data — until you unsubscribe.</li>
    </ul>
    <h2>6. Your rights</h2>
    <p>
      Under GDPR you have the right to access, rectify, delete, restrict, port, and object to processing of your personal data,
      and to lodge a complaint with the Portuguese Data Protection Authority (CNPD) at{" "}
      <a href="https://www.cnpd.pt" target="_blank" rel="noreferrer">cnpd.pt</a>.
    </p>
    <p>
      To exercise any right, email <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>. We respond within 30 days.
    </p>
  </>
);

const Cookies = () => (
  <>
    <p>
      This Cookie Policy explains what cookies and similar technologies {COMPANY.site} uses.
    </p>
    <h2>1. What is a cookie?</h2>
    <p>
      A cookie is a small text file stored on your device when you visit a website. We also use browser <em>localStorage</em>{" "}
      for the same purpose. We treat both the same way.
    </p>
    <h2>2. Cookies we use</h2>
    <ul>
      <li>
        <strong>Strictly necessary</strong> (no consent required) — <em>gl_lang</em> (your language preference), <em>gl_token</em>{" "}
        (your login session), <em>gl_cart</em> (items in your bag). Without these the site cannot function.
      </li>
      <li>
        <strong>Consent — cookie preferences</strong> — <em>gl_cookie_consent</em> records whether you have dismissed the cookie
        banner.
      </li>
      <li>
        <strong>Third-party</strong> — Stripe sets its own cookies on the checkout page for fraud detection. Their policy:{" "}
        <a href="https://stripe.com/privacy" target="_blank" rel="noreferrer">stripe.com/privacy</a>.
      </li>
    </ul>
    <p>
      We do <strong>not</strong> currently use any advertising, retargeting, or third-party analytics cookies.
    </p>
    <h2>3. How to control cookies</h2>
    <p>
      Most browsers let you block or delete cookies via the settings. Blocking strictly necessary cookies will break the shop
      (you won't be able to sign in or add to bag).
    </p>
    <h2>4. Contact</h2>
    <p>
      Questions? Email <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>.
    </p>
  </>
);

const CONTENT = { terms: <Terms />, privacy: <Privacy />, cookies: <Cookies /> };

const Legal = () => {
  const { topic } = useParams();
  const key = TITLES[topic] ? topic : "terms";
  const title = TITLES[key];
  useSEO({ title: `${title} · GOYA LISBOA`, noindex: true });

  return (
    <div className="pt-28 md:pt-36 pb-24 px-6 md:px-12 max-w-3xl mx-auto" data-testid={`legal-${key}`}>
      <div className="font-badge text-[10px] tracking-[0.32em] uppercase text-[var(--gl-orange-hover)]">Legal</div>
      <h1 className="font-serif-display text-4xl md:text-6xl mt-3 tracking-tight text-[#241C18]">{title}</h1>
      <div className="mt-3 text-xs font-badge tracking-[0.22em] text-[#8A7568]">
        Last updated · February 2026
      </div>

      <article className="legal-prose mt-10 text-[#241C18] text-base leading-relaxed">
        {CONTENT[key]}
      </article>

      <div className="mt-16 flex flex-wrap gap-4 text-sm">
        <Link to="/legal/terms" className={`underline ${key === "terms" ? "text-[var(--gl-orange-hover)]" : ""}`}>Terms & Conditions</Link>
        <Link to="/legal/privacy" className={`underline ${key === "privacy" ? "text-[var(--gl-orange-hover)]" : ""}`}>Privacy Policy</Link>
        <Link to="/legal/cookies" className={`underline ${key === "cookies" ? "text-[var(--gl-orange-hover)]" : ""}`}>Cookie Policy</Link>
      </div>
      <div className="mt-10">
        <Link to="/" className="btn-orange" data-testid="legal-back-home">← Back to shop</Link>
      </div>
    </div>
  );
};

export default Legal;
